import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Layout from "@/components/Layout";
import { ArrowRight, Eye, Zap, Shield, Users, Award, ChevronRight, Building2, Globe, Cpu } from "lucide-react";
import { Badge } from "@/components/ui/badge";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
const Index = () => {
  const features = [
    {
      icon: <Eye className="w-8 h-8 text-blue-600" />,
      title: "高精度光学计算引擎",
      description: "自主研发的光线追迹与光学仿真引擎，支持复杂光学系统的高精度建模与仿真，为光学设计提供核心技术支撑"
    },
    {
      icon: <Zap className="w-8 h-8 text-blue-600" />,
      title: "人工智能与智能算法",
      description: "深度融合AI技术与光学机理，实现遥感影像智能解译、光场智能控制等创新应用"
    },
    {
      icon: <Shield className="w-8 h-8 text-blue-600" />,
      title: "多源数据处理平台",
      description: "构建大数据处理与存储系统，支持卫星遥感、气象海洋等多源数据的处理与分析"
    }
  ];

  const dataServices = [
    {
      slug: "building-environment",
      icon: <Building2 className="w-7 h-7 text-white" />,
      title: "建筑设施与环境数据服务",
      badge: "智慧建筑 · 绿色环境",
      description: "提供建筑设施数据治理与智能分析一体化服务，构建数字孪生模型实现环境数据处理与能效优化。",
      highlights: ["环境数据处理", "异常预警预测", "设备预测维护"],
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      slug: "remote-sensing",
      icon: <Globe className="w-7 h-7 text-white" />,
      title: "遥感影像图像数据处理",
      badge: "遥感解译 · 地理信息",
      description: "提供卫星、航空及无人机遥感影像的全链条处理解决方案，支持多源数据批量化预处理与智能解译。",
      highlights: ["多源数据处理", "辐射几何校正", "语义分割分类", "长时序变化检测"],
      gradient: "from-indigo-500 to-blue-600"
    },
    {
      slug: "optical-simulation",
      icon: <Cpu className="w-7 h-7 text-white" />,
      title: "光学仿真数据建模与数字孪生",
      badge: "光学计算 · 数字孪生",
      description: "将光学机理建模与大数据分析深度融合，基于自主光线追迹引擎构建光学系统性能数字孪生体。",
      highlights: ["光谱仿真建模", "数字孪生构建", "统计公差分析", "装配工艺优化"],
      gradient: "from-cyan-500 to-blue-600"
    }
  ];

  const products = [
    {
      name: "几何光学仿真",
      title: "几何光学仿真",
      description: "突破光学系统的高精度光线追迹、多变量多目标的自动优化和公差分析等关键技术",
      image: "./images/seeod产品图片.png",
      features: ["自动绘图", "波动像分析", "公差分析", "成像模拟"]
    },
    // {
    //   name: "SeeLight",
    //   title: "物理光学仿真",
    //   description: "专业级的激光/光纤仿真软件，72个基础光学元件模型，满足不同场景的仿真模拟需求",
    //   image: "./images/seelight产品图片.jpg",
    //   features: ["72个基础光学元件", "8个光纤激光元件", "52个应用实例", "教学研究适用"]
    // },
    {
      name: "薄膜优化与误差分析",
      title: "薄膜优化与误差分析",
      description: "光学薄膜系统优化设计软件，支持多种膜系设计方法和参数优化",
      image: "./images/SeeThinFilm产品图片.png",
      features: ["复合薄膜光谱仿真", "膜系设计分析", "公差分析", "误差预测"]
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                光学智能计算
                <span className="block text-blue-600 mt-6">为仿真增质提效</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                杭州光智云软件有限公司专注于光学智能算法，致力于光学软件研发。
                基于物理机理进行智能信息处理，将传统光学和现代AI技术充分融合。
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" asChild className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800">
                  <Link to="/trial">
                    免费试用 <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/products">
                    了解产品
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-600 rounded-3xl transform rotate-3 opacity-20"></div>
              <Carousel opts={{ loop: true }} plugins={[Autoplay({ delay: 5000 })]} className="relative rounded-2xl shadow-2xl w-full h-auto overflow-hidden">
                <CarouselContent>
                  <CarouselItem className="flex items-center justify-center">
                    <img 
                      src="./images/公司图片.png" 
                      alt="光智云产品展示" 
                      className="w-full h-auto object-cover"
                    />
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="-left-4" />
                <CarouselNext className="-right-4" />
              </Carousel>
            </div>
          </div>
        </div>
      </section>

      {/* Business Scope Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            {/* <Badge variant="outline" className="mb-4 text-sm border-blue-300 text-blue-700 bg-blue-50">
              企业资质
            </Badge> */}
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              多元化业务布局
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              杭州光智云软件有限公司以光学智能计算为核心，构建了涵盖软件开发、智能数据处理、精密仪器制造与技术服务的完整产业生态链
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "软件与信息技术服务",
                items: ["软件开发与销售", "人工智能应用软件", "信息技术咨询", "计算机软硬件服务"],
                icon: <Cpu className="w-6 h-6" />,
                color: "from-blue-500 to-blue-600"
              },
              {
                title: "智能数据处理服务",
                items: ["大数据处理与分析", "卫星遥感数据处理", "互联网数据服务", "数据存储支持系统"],
                icon: <Globe className="w-6 h-6" />,
                color: "from-cyan-500 to-blue-600"
              },
              {
                title: "专业技术服务",
                items: ["遥感图像智能处理", "光场智能控制", "光学仿真建模", "数字孪生技术"],
                icon: <Zap className="w-6 h-6" />,
                color: "from-blue-600 to-purple-600"
              }
            ].map((category, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-4 text-white group-hover:scale-110 transition-transform duration-300`}>
                    {category.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {category.title}
                  </h3>
                  <ul className="space-y-2">
                    {category.items.map((item, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <ChevronRight className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            {/* <Badge variant="outline" className="mb-4 text-sm border-blue-300 text-blue-700 bg-blue-50">
              技术实力
            </Badge> */}
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              核心技术优势
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              融合光学计算、人工智能与大数据技术，为国防军事、航空航天、高端装备制造等领域提供核心技术支撑
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 group">
                <CardContent className="p-8 text-center">
                  <div className="mb-6 flex justify-center">
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full group-hover:scale-110 transition-transform duration-300">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            {/* <Badge variant="outline" className="mb-3 text-sm border-blue-300 text-blue-700 bg-blue-50">
              核心产品
            </Badge> */}
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              光学相关软件
            </h2>
            <p className="text-base text-gray-600">
              专业的光学仿真解决方案，满足不同应用场景需求
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {products.map((product, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-lg transition-all duration-300 group">
                <div className="flex flex-col sm:flex-row">
                  {/* <div className="sm:w-2/5 overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 min-h-[180px]"
                    />
                  </div> */}
                  <CardContent className="sm:w-3/5 p-5 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-bold text-blue-600">
                          {product.name}
                        </h3>
                        <span className="text-xs text-gray-500">|</span>
                        <h4 className="text-sm font-medium text-gray-700">
                          {product.title}
                        </h4>
                      </div>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {product.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {product.features.slice(0, 3).map((feature, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                    {/* <Button variant="ghost" size="sm" className="mt-3 w-fit p-0 h-auto text-blue-600 hover:text-blue-700 hover:bg-transparent" asChild>
                      <Link to="/products" className="text-sm flex items-center group/link">
                        了解更多 <ChevronRight className="w-4 h-4 ml-1 group-hover/link:translate-x-1 transition-transform" />
                      </Link>
                    </Button> */}
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Data Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              多源数据处理与智能分析服务
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto whitespace-nowrap">
              融合光学计算、遥感解译与人工智能技术，为政企客户提供精准、可靠的数字化决策支撑
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {dataServices.map((service, index) => (
              <Link
                key={index}
                to={`/data-service/${service.slug}`}
                className="group block"
              >
                <Card
                  className="cursor-pointer overflow-hidden border-0 shadow-lg hover:shadow-2xl group-hover:-translate-y-1 transition-all duration-300 h-full"
                >
                <div className={`relative h-32 bg-gradient-to-br ${service.gradient} overflow-hidden`}>
                  <div className="absolute inset-0 bg-white/10 backdrop-blur-[1px]"></div>
                  <div className="absolute -right-6 -top-6 w-32 h-32 bg-white/10 rounded-full"></div>
                  <div className="absolute -right-2 -bottom-8 w-24 h-24 bg-white/10 rounded-full"></div>
                  <div className="relative h-full flex items-center px-6">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 shadow-sm">
                      {service.icon}
                    </div>
                    <h3 className="ml-4 text-xl font-bold text-white leading-tight drop-shadow-sm">
                      {service.title}
                    </h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <Badge
                    variant="secondary"
                    className="mb-4 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                  >
                    {service.badge}
                  </Badge>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5 min-h-[60px]">
                    {service.description}
                  </p>
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {service.highlights.map((item, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600">
                        <ChevronRight className="w-4 h-4 text-blue-500 mr-1.5 flex-shrink-0" />
                        <span className="truncate">{item}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    className="w-full group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-colors"
                  >
                    了解详情 <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-4xl font-bold mb-2">10+</div>
              <div className="text-blue-100">年技术积累</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">100+</div>
              <div className="text-blue-100">合作客户</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">50+</div>
              <div className="text-blue-100">成功案例</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">24/7</div>
              <div className="text-blue-100">技术支持</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            开始您的光学仿真之旅
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            立即体验光学仿真软件，感受智能算法带来的效率提升
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800">
              <Link to="/trial">
                免费试用 <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/contact">
                联系我们
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
